import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, Form, Row, Col, Table, Spinner, Button, Tabs, Tab, Modal } from 'react-bootstrap';
import Select from 'react-select';
import { getGrafanaUrl } from '../../services/config';
import Analytics from '../Analytics';
import SubscriberAnalytics from '../SubscriberAnalytics';
import CampaignDeliveryReport from "../CampaignDeliveryReport";
import ClickReport from "../ClickReport";
import ClickEngagement from "../ClickEngagement";
import { useConfigBasedTimeFilter, getStatusText, getToggleButtonText } from '../../utils/timeFilter';
import { useReports } from '../../services/api/hooks/useReports';
import { useTags } from '../../services/api/hooks/useTags';
import { useCampaigns } from '../../services/api/hooks/useCampaigns';
import { useSubscribers } from '../../services/api/hooks/useSubscribers';
import { useTopics } from '../../services/api/hooks/useTopics';

interface TagOption {
  value: string;
  label: string;
}

interface ReportEntry {
  tag: string; // Changed to single tag for display
  subscriberCount: number;
  percentRemaining: string;
  percentOfInitial: string;
}



interface EmailTrackingData {
  email: string;
  campaign: string;
  timestamp: string;
  eventType: string;
  ipAddress?: string;
  reason?: string;
}

const Report = () => {
  const [selectedTags, setSelectedTags] = useState<TagOption[]>([]);
  const { showAll: showAllTags, setShowAll: setShowAllTags, timeFilter } = useConfigBasedTimeFilter();
  const [, setTagsLoaded] = useState(false);
  const [reportData, setReportData] = useState<ReportEntry[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [newReportName, setNewReportName] = useState('');
  const [isSavingReport, setIsSavingReport] = useState(false);
  const [selectedSavedReport, setSelectedSavedReport] = useState<TagOption | null>(null);
  const [currentSavedReportId, setCurrentSavedReportId] = useState<number | null>(null);
  const [isDeletingReport, setIsDeletingReport] = useState(false);
  const [topDomains, setTopDomains] = useState<string[]>([]);
  const [domainReports, setDomainReports] = useState<{ [key: string]: ReportEntry[] }>({});
  const [isFetchingDomains, setIsFetchingDomains] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState<number | null>(null);
  const [showCampaignAnalytics, setShowCampaignAnalytics] = useState(false);
  const [grafanaUrl, setGrafanaUrl] = useState<string>('');
  const [searchEmail, setSearchEmail] = useState('');
  const [emailTrackingData, setEmailTrackingData] = useState<EmailTrackingData[]>([]);
  const [isSearchingEmail, setIsSearchingEmail] = useState(false);
  const [showReasonModal, setShowReasonModal] = useState(false);
  const [selectedReason, setSelectedReason] = useState('');
  const [activeTab, setActiveTab] = useState('subscriber-reports');
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [subscriberAnalyticsLoading, setSubscriberAnalyticsLoading] = useState(false);
  const [selectedDays, setSelectedDays] = useState(30);
  const [showCustomDateRange, setShowCustomDateRange] = useState(false);
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [selectedTopicId, setSelectedTopicId] = useState<number | undefined>(undefined);
  const analyticsRef = useRef<{ refresh: () => void }>(null);

  const { data: allTags } = useTags({ type: 'recent', showAll: showAllTags });
  const allTagOptions = allTags.map(tag => ({ value: tag.name, label: tag.name }));
  const { data: campaigns } = useCampaigns();
  const { data: topics } = useTopics();

  // Set default topic when topics are loaded
  useEffect(() => {
    if (topics && topics.length > 0 && !selectedTopicId) {
      const defaultTopic = topics.find((t: any) => t.is_default);
      const defaultId = defaultTopic?.id || topics[0].id;
      setSelectedTopicId(defaultId);
    }
  }, [topics, selectedTopicId]);
  const { getTopDomains } = useSubscribers();
  const { 
    savedReports, 
    searchEmailTracking, 
    saveReport, 
    deleteReport, 
    getSubscriberCount 
  } = useReports();

  // Get default topic (always required)
  const defaultTopic = topics?.find((t: any) => t.is_default);
  const defaultTopicId = defaultTopic?.id || topics?.[0]?.id;

  const handleSearchEmailTracking = useCallback(async (email: string) => {
    setIsSearchingEmail(true);
    try {
      const data = await searchEmailTracking(email) as EmailTrackingData[];
      setEmailTrackingData(data);
    } catch (error) {
      console.error('Error searching email tracking data:', error);
      alert('Network error occurred while searching.');
      setEmailTrackingData([]);
    } finally {
      setIsSearchingEmail(false);
    }
  }, [searchEmailTracking]);

  const fetchSubscriberCount = useCallback(async (tags: string[], domain?: string) => {
    setIsLoading(true);
    try {
      return await getSubscriberCount(tags, domain);
    } catch (error) {
      console.error('Error fetching subscriber count:', error);
      return 0;
    } finally {
      setIsLoading(false);
    }
  }, [getSubscriberCount]);

  const generateReport = useCallback(async (tags: TagOption[]) => {
    const newReportData: ReportEntry[] = [];
    let previousCount = 0;
    let firstCount = 0;

    // Logic for the main report
    for (let i = 0; i < tags.length; i++) {
      const currentTags = tags.slice(0, i + 1).map((tag: TagOption) => tag.value);
      const currentCount = await fetchSubscriberCount(currentTags);

      if (i === 0) {
        firstCount = currentCount;
      }

      const displayTag = i === 0 ? tags[i].label : `+ ${tags[i].label}`;

      const percentRemaining = previousCount === 0 
        ? 'N/A' 
        : `${((currentCount / previousCount) * 100).toFixed(2)}%`;
      
      const percentOfInitial = firstCount === 0
        ? 'N/A'
        : `${((currentCount / firstCount) * 100).toFixed(2)}%`;

      newReportData.push({
        tag: displayTag,
        subscriberCount: currentCount,
        percentRemaining,
        percentOfInitial,
      });
      previousCount = currentCount;
    }
    setReportData(newReportData);

    // Logic for domain-specific reports
    if (tags.length > 0) {
      const newDomainReports: { [key: string]: ReportEntry[] } = {};

      setIsFetchingDomains(true);
      try {
        const domains = await getTopDomains(tags[0].value);
        setTopDomains(domains);

        for (const domain of domains) {
          let domainPreviousCount = 0; // Track previous count for % Remaining within this domain's report
          let domainFirstCount = 0; // Track first count for % of Initial within this domain's report
          const domainSpecificReportEntries: ReportEntry[] = [];

          for (let i = 0; i < tags.length; i++) {
            const currentTagsForDomain = tags.slice(0, i + 1).map((tag: TagOption) => tag.value);
            const currentCount = await fetchSubscriberCount(currentTagsForDomain, domain);

            if (i === 0) {
              domainFirstCount = currentCount;
            }

            const displayTag = i === 0 ? tags[i].label : `+ ${tags[i].label}`;

            const percentRemaining = domainPreviousCount === 0 
              ? 'N/A' 
              : `${((currentCount / domainPreviousCount) * 100).toFixed(2)}%`;
            
            const percentOfInitial = domainFirstCount === 0
              ? 'N/A'
              : `${((currentCount / domainFirstCount) * 100).toFixed(2)}%`;

            domainSpecificReportEntries.push({
              tag: displayTag,
              subscriberCount: currentCount,
              percentRemaining,
              percentOfInitial,
            });
            domainPreviousCount = currentCount;
          }
          newDomainReports[domain] = domainSpecificReportEntries;
        }
        setDomainReports(newDomainReports);
      } catch (error) {
        console.error('Error fetching top domains or initial domain counts:', error);
      } finally {
        setIsFetchingDomains(false);
      }
    } else {
      // No tags selected, clear domain reports
      setTopDomains([]);
      setDomainReports({});
    }
  }, [fetchSubscriberCount, getTopDomains]);

  useEffect(() => {
    getGrafanaUrl().then(setGrafanaUrl);
  }, []);

  useEffect(() => {
    // Handle URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const tabParam = urlParams.get('tab');
    const emailParam = urlParams.get('email');
    
    if (tabParam) {
      setTimeout(() => setActiveTab(tabParam), 100);
    }
    if (emailParam) {
      const decodedEmail = decodeURIComponent(emailParam);
      setTimeout(() => {
        setSearchEmail(decodedEmail);
        if (tabParam === 'email-search' && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(decodedEmail)) {
          setTimeout(() => {
            handleSearchEmailTracking(decodedEmail);
          }, 200);
        }
      }, 100);
    }
  }, [handleSearchEmailTracking]);

  const handleTagSelect = (selectedOption: any) => {
    const newSelectedTags = selectedOption ? [...selectedOption] : [];
    setSelectedTags(newSelectedTags);

    // Reset saved report selection when tags are manually changed
    setSelectedSavedReport(null);
    setCurrentSavedReportId(null);
    
    // Clear previous report data
    setReportData([]);
    setTopDomains([]);
    setDomainReports({});
  };

  const handleGenerateReport = () => {
    if (selectedTags.length === 0) {
      alert('Please select at least one tag to generate a report.');
      return;
    }
    generateReport(selectedTags);
  };

  const handleSaveReport = async () => {
    if (!newReportName.trim()) {
      alert('Please enter a name for the report.');
      return;
    }
    if (selectedTags.length === 0) {
      alert('Please select at least one tag to save the report.');
      return;
    }

    setIsSavingReport(true);
    try {
      await saveReport(newReportName, selectedTags.map(tag => tag.value));
      setNewReportName('');
      alert('Report saved successfully!');
    } catch (error) {
      console.error('Error saving report:', error);
      alert('An error occurred while saving the report.');
    } finally {
      setIsSavingReport(false);
    }
  };

  const handleLoadReport = (selectedOption: any) => {
    if (selectedOption) {
      setSelectedSavedReport(selectedOption);
      const reportToLoad = savedReports.find(report => report.id === Number(selectedOption.value));
      if (reportToLoad) {
        setCurrentSavedReportId(reportToLoad.id);
        const tagsToLoad = reportToLoad.tags.split(',').filter(tag => tag !== '').map(tag => ({
          value: tag,
          label: tag,
        }));
        setSelectedTags(tagsToLoad);
        generateReport(tagsToLoad);
      }
    } else {
      setSelectedSavedReport(null);
      setCurrentSavedReportId(null);
      setSelectedTags([]);
      setReportData([]);
      setTopDomains([]);
      setDomainReports({});
    }
  };

  const handleDeleteSavedReport = async () => {
    if (!currentSavedReportId) return;
    
    const reportToDelete = savedReports.find(report => report.id === currentSavedReportId);
    if (!reportToDelete) return;

    const confirmDelete = window.confirm(`Are you sure you want to delete the saved report "${reportToDelete.name}"? This action cannot be undone.`);
    if (!confirmDelete) return;

    setIsDeletingReport(true);
    try {
      await deleteReport(currentSavedReportId);
      setSelectedSavedReport(null);
      setCurrentSavedReportId(null);
      setSelectedTags([]);
      setReportData([]);
      setTopDomains([]);
      setDomainReports({});
      alert('Saved report deleted successfully!');
    } catch (error) {
      console.error('Error deleting saved report:', error);
      alert('Network error occurred while deleting the report');
    } finally {
      setIsDeletingReport(false);
    }
  };

  const handleCampaignAnalyticsSubmit = () => {
    if (!selectedCampaign) {
      alert('Please select a campaign.');
      return;
    }
    setShowCampaignAnalytics(true);
  };



  const handleEmailSearch = async () => {
    if (!searchEmail.trim()) {
      alert('Please enter an email address to search.');
      return;
    }
    await handleSearchEmailTracking(searchEmail);
  };

  return (
    <Card className="m-3">
      <Card.Header as="h5" style={{ backgroundColor: '#dcedc8' }}>Reports</Card.Header>
      <Card.Body>
        <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k || 'subscriber-reports')} id="reports-tabs">
          <Tab eventKey="subscriber-reports" title="Subscriber Reports">
            <Form>
              <Row className="mb-3">
                <Col md={12}>
                  <Form.Label>Load Saved Report:</Form.Label>
                  <Select
                    name="savedReports"
                    options={savedReports.map(report => ({ value: String(report.id), label: report.name }))}
                    className="basic-single"
                    classNamePrefix="select"
                    onChange={handleLoadReport}
                    value={selectedSavedReport}
                    placeholder="Load a saved report..."
                    isClearable
                  />
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={10}>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <Form.Label className="mb-0">Select Tags (in order of drill-down):</Form.Label>
                    <div className="d-flex align-items-center gap-2">
                      <span className="text-muted small">
                        {getStatusText(showAllTags, timeFilter)}
                      </span>
                      <Button 
                        variant="outline-secondary" 
                        size="sm"
                        onClick={() => {
                          setShowAllTags(!showAllTags);
                          setTagsLoaded(false);
                        }}
                      >
                        {getToggleButtonText(showAllTags, timeFilter)}
                      </Button>
                    </div>
                  </div>
                  <Select
                    isMulti
                    name="tags"
                    options={allTagOptions}
                    className="basic-multi-select"
                    classNamePrefix="select"
                    onChange={handleTagSelect}
                    value={selectedTags}
                    placeholder="Select tags to build your report..."
                    isClearable
                  />
                </Col>
                <Col md={2} className="d-flex align-items-end">
                  <Button 
                    onClick={handleGenerateReport} 
                    disabled={isLoading || selectedTags.length === 0}
                    variant="primary"
                  >
                    {isLoading ? <Spinner animation="border" size="sm" /> : "Generate Report"}
                  </Button>
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={8}>
                  <Form.Control
                    type="text"
                    placeholder="Name for this report combination"
                    value={newReportName}
                    onChange={(e) => setNewReportName(e.target.value)}
                    size="sm"
                  />
                </Col>
                <Col md={4}>
                  <Button onClick={handleSaveReport} disabled={isSavingReport || selectedTags.length === 0} size="sm">
                    {isSavingReport ? <Spinner animation="border" size="sm" /> : "Save Report"}
                  </Button>
                </Col>
              </Row>
            </Form>

            {isLoading ? (
              <div className="d-flex justify-content-center align-items-center" style={{ height: '100px' }}>
                <Spinner animation="border" role="status">
                  <span className="visually-hidden">Loading Report...</span>
                </Spinner>
              </div>
            ) : reportData.length > 0 && (
              <Table striped bordered hover responsive size="sm" className="mt-3">
                <thead>
                  <tr>
                    <th>Tag</th>
                    <th>Subscriber Count</th>
                    <th>% Remaining</th>
                    <th>% of Initial</th>
                  </tr>
                </thead>
                <tbody>
                  {reportData.map((entry, index) => (
                    <tr key={index}>
                      <td>{entry.tag}</td>
                      <td>{entry.subscriberCount}</td>
                      <td>{entry.percentRemaining}</td>
                      <td>{entry.percentOfInitial}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
            {reportData.length === 0 && !isLoading && selectedTags.length > 0 && (
              <p className="mt-3">No subscribers found for the selected tag combination.</p>
            )}

            {isFetchingDomains ? (
              <div className="d-flex justify-content-center align-items-center" style={{ height: '100px' }}>
                <Spinner animation="border" role="status">
                  <span className="visually-hidden">Loading Domain Reports...</span>
                </Spinner>
              </div>
            ) : topDomains.length > 0 && (
              <div className="mt-4">
                <Table striped bordered hover responsive size="sm">
                  <thead>
                    <tr>
                      <th>Tag</th>
                      <th>Subscriber Count</th>
                      <th>% Remaining</th>
                      <th>% of Initial</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topDomains.map(domain => (
                      <React.Fragment key={domain}>
                        <tr>
                          <td colSpan={4} className="text-start" style={{ backgroundColor: '#e3f2fd', fontWeight: 'bold' }}>
                            {domain}
                          </td>
                        </tr>
                        {domainReports[domain] && domainReports[domain].map((entry, index) => (
                          <tr key={`${domain}-${index}`}>
                            <td>{entry.tag}</td>
                            <td>{entry.subscriberCount}</td>
                            <td>{entry.percentRemaining}</td>
                            <td>{entry.percentOfInitial}</td>
                          </tr>
                        ))}
                      </React.Fragment>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}

            {/* Delete button for saved reports */}
            {currentSavedReportId && reportData.length > 0 && (
              <div className="mt-4 d-flex justify-content-end">
                <Button 
                  variant="outline-danger" 
                  onClick={handleDeleteSavedReport}
                  disabled={isDeletingReport}
                >
                  {isDeletingReport ? (
                    <>
                      <Spinner size="sm" className="me-2" />
                      Deleting...
                    </>
                  ) : (
                    'Delete Saved Report'
                  )}
                </Button>
              </div>
            )}
          </Tab>
          
          <Tab eventKey="subscriber-analytics" title="Subscriber Analytics">
            <div className="mt-3">
              <Form>
                <Row className="mb-3">
                  <Col md={3}>
                    <Form.Label>Time Period:</Form.Label>
                    <Select
                      options={[
                        { value: 7, label: 'Last 7 days' },
                        { value: 30, label: 'Last 30 days' },
                        { value: 90, label: 'Last 90 days' },
                        { value: 'custom', label: 'Custom Date Range' }
                      ]}
                      value={showCustomDateRange ? { value: 'custom', label: 'Custom Date Range' } : { value: selectedDays, label: `Last ${selectedDays} days` }}
                      className="basic-single"
                      classNamePrefix="select"
                      onChange={(option: any) => {
                        if (option?.value === 'custom') {
                          setShowCustomDateRange(true);
                        } else {
                          setShowCustomDateRange(false);
                          setSelectedDays(option.value);
                        }
                      }}
                    />
                  </Col>
                  {showCustomDateRange && (
                    <Col md={6}>
                      <Row>
                        <Col md={6}>
                          <Form.Label>Start Date:</Form.Label>
                          <Form.Control 
                            type="date" 
                            value={customStartDate}
                            onChange={(e) => setCustomStartDate(e.target.value)}
                          />
                        </Col>
                        <Col md={6}>
                          <Form.Label>End Date:</Form.Label>
                          <Form.Control 
                            type="date" 
                            value={customEndDate}
                            onChange={(e) => setCustomEndDate(e.target.value)}
                          />
                        </Col>
                      </Row>
                    </Col>
                  )}
                  <Col md={3}>
                    <Form.Label>Topic:</Form.Label>
                    <Form.Select
                      value={selectedTopicId || defaultTopicId}
                      onChange={(e) => setSelectedTopicId(parseInt(e.target.value))}
                    >
                      {topics?.map((topic) => (
                        <option key={topic.id} value={topic.id}>
                          {topic.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Col>
                  <Col md={2} className="d-flex align-items-end">
                    <Button 
                      onClick={() => {
                        if (showCustomDateRange) {
                          if (!customStartDate || !customEndDate) {
                            alert('Please select both start and end dates');
                            return;
                          }
                        }
                        setSubscriberAnalyticsLoading(true);
                      }}
                      disabled={subscriberAnalyticsLoading}
                      variant="primary"
                    >
                      {subscriberAnalyticsLoading ? <Spinner animation="border" size="sm" /> : "Generate Report"}
                    </Button>
                  </Col>
                </Row>
              </Form>
              <SubscriberAnalytics 
                days={selectedDays}
                startDate={customStartDate}
                endDate={customEndDate}
                trigger={subscriberAnalyticsLoading}
                onLoadingChange={setSubscriberAnalyticsLoading}
                topicId={selectedTopicId}
              />
            </div>
          </Tab>
          
          <Tab eventKey="campaign-analytics" title="Campaign Analytics">
            <div className="mt-3">
              <Form>
                <Row className="mb-3">
                  <Col md={8}>
                    <Form.Label>Select Campaign:</Form.Label>
                    <Select
                      name="campaign"
                      options={campaigns.map(campaign => ({ 
                        value: campaign.id, 
                        label: campaign.topic_name ? `${campaign.topic_name}: ${campaign.name}` : campaign.name 
                      }))}
                      className="basic-single"
                      classNamePrefix="select"
                      onChange={(selectedOption: any) => {
                        setSelectedCampaign(selectedOption ? selectedOption.value : null);
                        setShowCampaignAnalytics(false);
                      }}
                      placeholder="Select a campaign..."
                      isClearable
                    />
                  </Col>
                  <Col md={4} className="d-flex align-items-end gap-2">
                    <Button 
                      onClick={handleCampaignAnalyticsSubmit}
                      disabled={!selectedCampaign}
                      variant="primary"
                    >
                      Show Analytics
                    </Button>
                    {showCampaignAnalytics && selectedCampaign && !analyticsLoading && (
                      <Button 
                        onClick={() => analyticsRef.current?.refresh()}
                        variant="outline-primary"
                        title="Refresh analytics data"
                      >
                        Refresh
                      </Button>
                    )}
                  </Col>
                </Row>
              </Form>
              
              {showCampaignAnalytics && selectedCampaign && (
                <Analytics ref={analyticsRef} campaignId={selectedCampaign} onLoadingChange={setAnalyticsLoading} />
              )}
            </div>
          </Tab>
          
          <Tab eventKey="delivery-report" title="Campaign Delivery Report">
            <div className="mt-3">
              <CampaignDeliveryReport />
            </div>
          </Tab>

          <Tab eventKey="click-report" title="Click Report" mountOnEnter>
            <div className="mt-3">
              <ClickReport />
            </div>
          </Tab>
          <Tab eventKey="click-engagement" title="Click Engagement" mountOnEnter>
            <div className="mt-3">
              <ClickEngagement />
            </div>
          </Tab>
          <Tab eventKey="email-search" title="Email Search">
            <div className="mt-3">
              <Form>
                <Row className="mb-3">
                  <Col md={8}>
                    <Form.Label>Search Email Address:</Form.Label>
                    <Form.Control
                      type="email"
                      placeholder="Enter email address to search tracking data..."
                      value={searchEmail}
                      onChange={(e) => setSearchEmail(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleEmailSearch();
                        }
                      }}
                    />
                  </Col>
                  <Col md={4} className="d-flex align-items-end gap-2">
                    <Button 
                      onClick={handleEmailSearch}
                      disabled={isSearchingEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(searchEmail)}
                      variant="primary"
                    >
                      {isSearchingEmail ? <Spinner animation="border" size="sm" /> : "Search"}
                    </Button>
                    <Button 
                      onClick={handleEmailSearch}
                      disabled={isSearchingEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(searchEmail)}
                      variant="outline-secondary"
                    >
                      Refresh
                    </Button>
                  </Col>
                </Row>
              </Form>

              {isSearchingEmail ? (
                <div className="d-flex justify-content-center align-items-center" style={{ height: '100px' }}>
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Searching...</span>
                  </Spinner>
                </div>
              ) : emailTrackingData.length > 0 ? (
                <Table striped bordered hover responsive size="sm">
                  <thead>
                    <tr>
                      <th>Campaign</th>
                      <th>Event Type</th>
                      <th>Timestamp</th>
                      <th>IP Address</th>
                      <th>Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {emailTrackingData.map((entry, index) => (
                      <tr key={index}>
                        <td>{entry.campaign}</td>
                        <td>
                          <span className={`badge ${
                            entry.eventType === 'open' ? 'bg-info' :
                            entry.eventType === 'click' ? 'bg-success' :
                            entry.eventType === 'bounce' ? 'bg-danger' :
                            entry.eventType === 'delivery' ? 'bg-primary' : 'bg-secondary'
                          }`}>
                            {entry.eventType}
                          </span>
                        </td>
                        <td>{entry.timestamp && !isNaN(new Date(entry.timestamp).getTime()) ? new Date(entry.timestamp).toLocaleString() : ''}</td>
                        <td>{entry.ipAddress}</td>
                        <td>
                          {entry.reason ? (
                            <>
                              <span>{entry.reason.length > 50 ? `${entry.reason.substring(0, 50)}...` : entry.reason}</span>
                              {entry.reason.length > 50 && (
                                <Button 
                                  size="sm" 
                                  variant="link" 
                                  className="p-0 ms-1"
                                  onClick={() => {
                                    setSelectedReason(entry.reason || '');
                                    setShowReasonModal(true);
                                  }}
                                >
                                  Show More
                                </Button>
                              )}
                            </>
                          ) : ''}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : null}
            </div>
          </Tab>
          
          <Tab eventKey="mail-server" title="Mail Server Analytics">
            <div className="mt-3">
              <div className="text-center">
                <h5>Grafana Dashboard</h5>
                <p>Access detailed mail server analytics and monitoring data.</p>
                <Button 
                  variant="primary" 
                  href={grafanaUrl} 
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Open Grafana Dashboard
                </Button>
              </div>
            </div>
          </Tab>
          
          <Tab eventKey="isp-registration" title="ISP Registration">
            <div className="mt-3">
              <h5>📧 ISP Registration Links</h5>
              <p>Register with major email providers to improve deliverability and sender reputation.</p>
              
              <Row className="g-3">
                <Col md={6}>
                  <Card className="h-100">
                    <Card.Body>
                      <Card.Title>Gmail/Google</Card.Title>
                      <Card.Text>Register with Google Postmaster Tools for Gmail delivery insights.</Card.Text>
                      <Button variant="primary" href="https://postmaster.google.com/" target="_blank" rel="noopener noreferrer">
                        Register with Google
                      </Button>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={6}>
                  <Card className="h-100">
                    <Card.Body>
                      <Card.Title>Microsoft/Outlook</Card.Title>
                      <Card.Text>Register with Microsoft SNDS for Outlook.com and Hotmail delivery.</Card.Text>
                      <Button variant="primary" href="https://sendersupport.olc.protection.outlook.com/snds/" target="_blank" rel="noopener noreferrer">
                        Register with Microsoft
                      </Button>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={6}>
                  <Card className="h-100">
                    <Card.Body>
                      <Card.Title>Yahoo/Verizon</Card.Title>
                      <Card.Text>Register with Yahoo Sender Hub for Yahoo Mail delivery optimization.</Card.Text>
                      <Button variant="primary" href="https://senders.yahooinc.com/" target="_blank" rel="noopener noreferrer">
                        Register with Yahoo
                      </Button>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </div>
          </Tab>
        </Tabs>
      </Card.Body>
      
      {/* Reason Modal */}
      <Modal show={showReasonModal} onHide={() => setShowReasonModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Full Reason</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {selectedReason}
          </pre>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowReasonModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Card>
  );
};

export default Report;